<template>
    <div id="lt-yantr" class="advanced-calc mortgage-payment-lite-calc">
        <div class="container-width">
            <div class="advanced-calc-filter">
                <ul class="filter-inputs">
                    <li>
                        <number-input
                            v-model="HomePrice"
                            :label="labelHomePrice"
                            inputmode="decimal"
                            @input="updateHomeInsurance"
                        />
                    </li>
                    <li>
                        <drop-down
                            v-model="LoanTermInYears"
                            :label="labelLoanType"
                            :options="LoanTermInYearsList"
                            :tooltip-link="LoanTypeTooltip"
                            @input="updateMortgageRate($event)"
                        />
                    </li>
                    <li class="percentage-amt">
                        <number-input
                            v-model="DownPayment"
                            :label="labelDownPayment"
                            :tooltip-link="DownPaymentTooltip"
                            inputmode="decimal"
                            @input="DownPaymentChange($event)"
                        />
                    </li>
                    <li class="percentage-input">
                        <number-input
                            v-model="DownPaymentPercent"
                            symbol="%"
                            symbol-aligned="right"
                            maxValue="100.00"
                            inputmode="decimal"
                            precision="2"
                            @input="DownPaymentPercentChange($event)"
                        />
                    </li>
                </ul>
                <!-- Advanced Options start -->
                <div v-show="ShowAdvancedOptions" class="advanced-options lite" :class="{ 'all-options': ShowAdvancedOptions, 'less-options' : !ShowAdvancedOptions}">
                    <ul class="filter-inputs">
                        <li class="inputs-subtext">
                            <number-input
                                v-model="HomeInsurance"
                                :label="labelHomeInsurance"
                                inputmode="decimal"
                            />
                            <span class="subtext-val">/year</span>
                        </li>
                        <li>
                            <number-input
                                v-model="MortgageRate"
                                :label="labelMortgageRate"
                                symbol="%"
                                symbol-aligned="right"
                                precision="3"
                                maxValue="100.000"
                                inputmode="decimal"
                                @input="restrictMortgageRateToMaxValue"
                            />
                        </li>
                        <li class="percentage-amt inputs-subtext">
                            <number-input
                                v-model="PropertyTaxes"
                                :label="labelPropertyTaxes"
                                inputmode="decimal"
                                @input="PropertyTaxesChange($event)"
                            />
                            <span class="subtext-val">/year</span>
                        </li>
                        <li class="percentage-input">
                            <number-input
                                v-model="PropertyTaxesPercent"
                                symbol="%"
                                symbol-aligned="right"
                                maxValue="100.00"
                                precision="2"
                                inputmode="decimal"
                                @input="PropertyTaxesPercentChange($event)"
                            />
                        </li>
                        <li class="inputs-subtext">
                            <number-input
                                v-model="HoaFees"
                                :label="labelHoaFees"
                                inputmode="decimal"
                                maxlength="6"
                            />
                            <span class="subtext-val">/month</span>
                        </li>
                        <li class="include-pmi">
                            <div class="form-checkbox">
                                <input id="includePMI" v-model="IsPmiInclude" type="checkbox" :disabled="CheckPMI===false" />
                                <label for="includePMI">Include PMI</label>
                                <lt-label :span="span" :tooltip-link="PrivateMortgageInsuranceTooltip" />
                            </div>
                        </li>
                    </ul>
                </div>
                <a v-show="ShowAdvancedOptions" href="javascript:void(0)" class="reset-calc hidden-xs" @click="resetFilter()"><span class="lt-icons lt4-Refresh"></span> Reset Calculator</a>
                <!-- Advanced Options end-->
                <button v-show="ShowAdvancedOptions" class="btn btn-blue recalculate" type="submit" @click="calculateAndViewMore()">{{ recalculateButtonText }}</button>
                <label class="show-hide-options" :class="{ 'hidden-xs': ShowAdvancedOptions}" @click="viewMoreClick()">
                    <span class="lt-icons" :class="{ 'lt4-MinusCircle': ShowAdvancedOptions, 'lt4-PlusCircle' : !ShowAdvancedOptions}"></span> {{ !ShowAdvancedOptions ? "" : "Hide" }} Advanced Options
                </label>
            </div>
            <div class="row calculation-section">
                <div class="col-xs-12 col-sm-6 col-md-7 left-section">
                    <div>
                        <h4>Your {{ PaymentFrequency }} payment will be:</h4>
                        <label class="homeprice" :class="{ 'red-text': !isNegativeAmount}">${{ currencyFilterWithPrecision(TotalMoPayment) }}</label>
                        <div class="view-free-offers">
                            <h4>We’ve found offers for this result:</h4>
                            <button class="btn btn-blue" type="submit" @click="redirect()">{{ DestinationButtonText }}</button>
                        </div>
                        <div>
                            <advertising-disclosures v-if="DisclosureText" :disclosure-text="DisclosureText" :disclosure-tokens="DisclosureTokens" />
                        </div>
                        <lt-logo v-if="ltLogo===1">
                            <small>NMLS#1136 Terms & Conditions Apply</small>
                        </lt-logo>
                    </div>
                </div>
                <div class="col-xs-12 col-sm-6 col-md-5 right-section text-center">
                    <h4>{{ capitalize(PaymentFrequency) }} payment breakdown</h4>
                    <div class="chart-parent">
                        <vue-chart type="doughnut" :data="chartData" :options="options" :width="chartWidth" :height="chartHeight" />
                        <div class="donut-inner">
                            <label>Total {{ PaymentFrequency }} Payment:</label>
                            <h3 :class="{ 'red-text': !isNegativeAmount}">${{ currencyFilterWithPrecision(TotalMoPayment) }}</h3>
                        </div>
                    </div>
                    <div class="monthly-budget">
                        <ul class="list-unstyled summary">
                            <li>
                                <label><span class="green-bg"></span> Principal & Interest:</label>
                                <span class="value" :class="{ 'red-text': !isNegativeAmount}">${{ currencyFilterWithPrecision(MoPrincipalIntestrest/Math.round(PayFreqInNumber/12)) }}</span>
                            </li>
                            <li>
                                <label><span class="red-bg"></span> Property Taxes:</label>
                                <span class="value">${{ currencyFilterWithPrecision(PropertyTaxesCurrency*12/PayFreqInNumber) }}</span>
                            </li>
                            <li>
                                <label><span class="blue-bg"></span> Homeowner’s Insurance:</label>
                                <span class="value">${{ currencyFilterWithPrecision(HomeInsuranceCurrency*12/PayFreqInNumber) }}</span>
                            </li>
                            <li>
                                <label><span class="purple-bg"></span> HOA Fees:</label>
                                <span class="value">${{ currencyFilterWithPrecision(HoaFees*12/PayFreqInNumber) }}</span>
                            </li>
                            <li>
                                <label><span class="orange-bg"></span> PMI:</label>
                                <span class="value">${{ currencyFilterWithPrecision(PmiCurrency*12/PayFreqInNumber) }}</span>
                            </li>
                            <li>
                                <label><span class="dark-red-bg"></span> Extra Payment:</label>
                                <span class="value">${{ currencyFilterWithPrecision(MoExtraPayment) }}</span>
                            </li>
                        </ul>
                        <div class="clear-fix"></div>
                    </div>
                </div>
                <div v-if="disclosureStatement===1" class="col-xs-12 disclosure-block">
                    This <a :href="mortgageCalcUrl" target="_blank">mortgage calculator from LendingTree</a> is an estimate only and is not intended to be interpreted as a firm offers to lend funds. Please contact LendingTree to find a lender to give a loan quote specific to your situation.
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import VueChart from 'vue-chart-js';
    import Accounting from 'accounting';
    import NumberInput from '~/components/number-input.vue';
    import DropDown from '~/components/drop-down.vue';
    import DefaultTooltip from '~/constants/tooltip-defaults';
    import CreditScoreList from '~/constants/credit-score-list';
    import LoanTermList from '~/constants/loan-term-list';
    import LtLabel from '~/components/lt-label.vue';
    import ChartTooltip from '~/helpers/chart-tooltip';
    import PaymentHelper from '~/helpers/payment';
    import BaseMixin from '~/mixin/base-mixin';
    import TokenService from '~/services/token';
    import GetLocation from '~/mixin/get-geo-location';
    import StringFormat from '~/helpers/string-format';
    import LtLogo from '~/components/lt-logo.vue';
    import AdvertisingDisclosures from '~/components/advertising-disclosures.vue';

    export default {
        props: {
            labelPropertyLocation: {
                default: 'Property Location:',
            },
            labelHomePrice: {
                default: 'Home Price:',
            },
            labelDownPayment: {
                default: 'Down Payment:',
            },
            customizeButtonText: {
                default: 'Customize',
            },
            labelLoanType: {
                default: 'LoanType:',
            },
            labelMortgageRate: {
                default: 'Mortgage Rate:',
            },
            labelPropertyTaxes: {
                default: 'Property Taxes:',
            },
            labelHomeInsurance: {
                default: 'Home Insurance:',
            },
            labelHoaFees: {
                default: 'HOA Fees:',
            },
            homePrice: {
                default: '350000',
            },
            downPayment: {
                default: '70000',
            },
            mortgageRate: {
                default: '3.630',
            },
            propertyTaxes: {
                default: '1.25',
            },
            homeInsurance: {
                default: '1225',
            },
            hoaFees: {
                default: '50',
            },
            creditScore: {
                default: '2',
            },
            loanTermInYears: {
                default: 30,
            },
            downPaymentTooltip: {
                default: DefaultTooltip.DownPayment,
            },
            loanTypeTooltip: {
                default: DefaultTooltip.LoanType,
            },
            privateMortgageInsuranceTooltip: {
                default: DefaultTooltip.PrivateMortgageInsurance,
            },
            showAdvancedOptions: {
                default: false,
            },
            span: {
                type: String,
                default: '',
            },
            recalculateButtonText: {
                default: 'Recalculate',
            },
            maxValue: {
                default: 999999999,
            },
            checkPMI: {
                default: false,
            },
            oneTimeExpenseRate: {
                default: '3',
            },
            pmiPerc: {
                default: '0.00625',
            },
            moExtraPayment: {
                default: '0',
            },
            totalMoPayment: {
                default: '2083',
            },
            homeInsurancePerc: {
                default: '0.35',
            },
            ltLogo: {
                default: 0,
            },
            disclosureTokens: {
                default: '01',
            },
            disclosureText: {
                default: 'Disclosures',
            },
            disclosureStatement: {
                default: 0,
            },
            mortgageCalcUrl: {
                default: '/mortgage-calculator',
            },
            destinationLinkOrId: {
                default: 'wp-mortgage',
            },
            destinationButtonText: {
                default: 'View Free Offers',
            },
            downPaymentPercent: {
                default: 20.00,
            },
            propertyTaxesPercent: {
                default: 1.25,
            },
            totalInterestTooltip: {
                default: DefaultTooltip.TotalInterest,
            },
            totalLoanAmountTooltip: {
                default: DefaultTooltip.TotalLoanAmount,
            },
            totalOfAllPaymentTooltip: {
                default: DefaultTooltip.TotalOfAllPayment,
            },
        },
        data() {
            return {
                HomePrice: this.homePrice,
                DownPayment: this.downPayment,
                MortgageRate: parseFloat(this.mortgageRate),
                PropertyTaxes: parseFloat(this.propertyTaxes),
                HomeInsurance: this.homeInsurance,
                HoaFees: parseInt(this.hoaFees, 10),
                LoanTermInYears: this.loanTermInYears,
                CreditScoreList: CreditScoreList.homeaffordability,
                LoanTermInYearsList: LoanTermList.LoanTermInYearsList.refinance,
                DownPaymentTooltip: parseInt(this.hideTooltip, 10) ? '' : this.downPaymentTooltip,
                LoanTypeTooltip: parseInt(this.hideTooltip, 10) ? '' : this.loanTypeTooltip,
                PrivateMortgageInsuranceTooltip: parseInt(this.hideTooltip, 10) ? '' : this.privateMortgageInsuranceTooltip,
                TotalInterestTooltip: parseInt(this.hideTooltip, 10) ? '' : this.totalInterestTooltip,
                ShowAdvancedOptions: this.showAdvancedOptions,
                DownPaymentPercent: Accounting.formatNumber(this.downPaymentPercent, { precision: 2 }),
                PropertyTaxesPercent: this.propertyTaxesPercent,
                CheckPMI: this.checkPMI,
                IsPmiInclude: this.checkPMI,
                PmiPerc: parseFloat(this.pmiPerc),
                MoPrincipalIntestrest: this.moPrincipalIntestrest,
                PropertyTaxesCurrency: this.propertyTaxesCurrency,
                HomeInsuranceCurrency: this.homeInsuranceCurrency,
                PmiCurrency: this.pmiCurrency,
                TotalMoPayment: this.totalMoPayment,
                MoExtraPayment: parseInt(this.moExtraPayment, 10),
                CreditScore: this.creditScore,
                HomeInsurancePerc: parseFloat(this.homeInsurancePerc),
                MonthlyInterestRatePerc: this.monthlyInterestRatePerc,
                MortgageAmount: this.mortgageAmount,
                OneTimeExpenses: this.oneTimeExpenses,
                DownPaymentExpenses: this.downPaymentExpenses,
                PmiAmount: this.pmiAmount,
                DisclosureText: this.disclosureText,
                DisclosureTokens: this.disclosureTokens,
                InitialPropertyTax: this.PropertyTaxes,
                InitialPropertyTaxPercent: this.PropertyTaxesPercent,
                InitialDownPayment: this.DownPayment,
                InitialDownPaymentPercent: this.DownPaymentPercent,
                InitialHomePrice: this.homePrice,
                // Setting for chart
                chartData: {
                    labels: [],
                    datasets: [
                        {
                            data: [],
                            backgroundColor: [
                                '#08c177',
                                '#da3551',
                                '#00aeef',
                                '#2087c1',
                                '#ff704d',
                                '#d60000',
                            ],
                            borderWidth: 0,
                        },
                    ],
                },
                options: {
                    cutoutPercentage: 89,
                    legend: {
                        display: false,
                    },
                    tooltips: {
                        enabled: false,
                        custom: ChartTooltip.customTooltips,
                    },
                },
                chartWidth: 200,
                chartHeight: 130,
                PaymentFrequency: 'monthly',
                TempLocData: this.geoLocation,
                DestinationLinkOrId: this.destinationLinkOrId,
                DestinationButtonText: this.destinationButtonText,
            };
        },
        computed: {
            isNegativeAmount() {
                return this.TotalMoPayment >= 0;
            },
            intLoanTermInYears() {
                return parseInt(this.LoanTermInYears, 10);
            },
            getFieldValue() {
                return this.unformat(this.HomePrice) + this.DownPayment + this.CreditScore + this.LoanTermInYears + this.MortgageRate + this.PropertyTaxes + this.HomeInsurance + this.HoaFees + this.IsPmiInclude + this.MoExtraPayment + this.PaymentFrequency;
            },
            PayFreqInNumber() {
                return this.PaymentFrequency === 'bi-weekly'
                    ? 26
                    : this.PaymentFrequency === 'weekly'
                        ? 52
                        : 12;
            },
        },
        methods: {
            restrictMortgageRateToMaxValue() {
                this.MortgageRate = this.unformat(this.MortgageRate) > 100 ? 100.000 : this.MortgageRate;
            },
            unformat(val) {
                return Accounting.unformat(val);
            },
            calculateAndViewMore() {
                this.calculatePayment(this.MortgageRate);
                this.viewMoreClick();
            },
            viewMoreClick() {
                this.ShowAdvancedOptions = !this.ShowAdvancedOptions;
            },
            calculatePayment(val) {
                const payFreq = this.PaymentFrequency === 'bi-weekly'
                    ? 26
                    : this.PaymentFrequency === 'weekly'
                        ? 52
                        : 12;
                this.interestRate = val;
                this.MortgageAmount = this.unformat(this.HomePrice) - this.unformat(this.HomePrice) * (this.unformat(this.DownPayment) / this.unformat(this.HomePrice) * 100 / 100);
                this.OneTimeExpenses = this.unformat(this.HomePrice) * 3 / 100;
                this.DownPaymentExpenses = parseFloat(this.unformat(this.DownPayment)) + parseFloat(this.OneTimeExpenses);
                this.MonthlyInterestRatePerc = this.interestRate / 1200;
                this.oneTimeExpense = this.homePrice * (parseFloat(this.oneTimeExpenseRate) / 100);
                let percentValue = parseFloat(this.PropertyTaxesPercent).toFixed(2);
                percentValue = this.unformat(this.PropertyTaxesPercent) <= 100.00 ? percentValue : 100.00;
                this.propertyTaxesPerYear = this.unformat(this.HomePrice) * (this.unformat(percentValue) / 100);
                if (!this.IsPmiInclude) {
                    this.PmiPerc = 0;
                } else {
                    this.PmiPerc = parseFloat(this.pmiPerc);
                }
                this.PmiAmount = this.MortgageAmount * this.PmiPerc;
                this.AmortizationPeriodInMonths = this.LoanTermInYears * 12;
                this.MoPrincipalIntestrest = PaymentHelper.getMonthlyPrincipalAndIntreset(this.MortgageAmount, this.AmortizationPeriodInMonths, this.MonthlyInterestRatePerc);
                this.PropertyTaxesCurrency = this.propertyTaxesPerYear / 12;
                this.HomeInsuranceCurrency = this.unformat(this.HomeInsurance) / 12;
                this.PmiCurrency = this.PmiAmount / 12;
                this.TotalMoPayment = this.MoPrincipalIntestrest / Math.round(payFreq / 12) + this.MoExtraPayment + this.PropertyTaxesCurrency * 12 / payFreq + this.HomeInsuranceCurrency * 12 / payFreq + this.PmiCurrency * 12 / payFreq + this.HoaFees * 12 / payFreq;

                /* Monthly payment breakdown doughnut chart */
                this.chartData.labels = [];
                this.chartData.datasets[0].data = [];
                this.chartData.labels.push(`Principal & Interest ${Accounting.formatMoney(this.MoPrincipalIntestrest, { precision: 2 })}`);
                this.chartData.labels.push(`Property Taxes ${Accounting.formatMoney(this.PropertyTaxesCurrency, { precision: 2 })}`);
                this.chartData.labels.push(`Homeowner’s Insurance ${Accounting.formatMoney(this.HomeInsuranceCurrency, { precision: 2 })}`);
                this.chartData.labels.push(`HOA Fees ${Accounting.formatMoney(this.HoaFees, { precision: 2 })}`);
                this.chartData.labels.push(`PMI ${Accounting.formatMoney(this.PmiCurrency, { precision: 2 })}`);
                this.chartData.labels.push(`Extra Payment ${Accounting.formatMoney(this.MoExtraPayment, { precision: 2 })}`);

                const paymentPercent = Math.round(Math.abs(this.MoPrincipalIntestrest / this.TotalMoPayment * 100));
                const porpertyTxPercent = Math.round(Math.abs(this.PropertyTaxesCurrency / this.TotalMoPayment * 100));
                const insurancePercent = Math.round(Math.abs(this.HomeInsuranceCurrency / this.TotalMoPayment * 100));
                const hoaPercent = Math.round(Math.abs(this.HoaFees / this.TotalMoPayment * 100));
                const pmiPercent = Math.round(Math.abs(this.PmiCurrency / this.TotalMoPayment * 100));
                const moExtraPercent = Math.round(Math.abs(this.MoExtraPayment / this.TotalMoPayment * 100));

                this.chartData.datasets[0].data.push(paymentPercent);
                this.chartData.datasets[0].data.push(porpertyTxPercent);
                this.chartData.datasets[0].data.push(insurancePercent);
                this.chartData.datasets[0].data.push(hoaPercent);
                this.chartData.datasets[0].data.push(pmiPercent);
                this.chartData.datasets[0].data.push(moExtraPercent);
                this.DownPayPercentage = this.DownPaymentPercent.toString().length >= 6 && this.DownPaymentPercent < 100 ? this.DownPaymentPercent.toFixed(2) : this.DownPaymentPercent;
                this.CheckPMI = !(this.DownPayPercentage >= 20);
                if (this.CheckPMI === false) this.IsPmiInclude = false;
            },
            updateInitialInterestRate(val) {
                this.MortgageRate = val;
                this.calculatePayment(val);
            },
            updateMortgageRateValue(val) {
                this.MortgageRate = val;
            },
            currencyFilter(figure) {
                return Accounting.formatNumber(Math.abs(figure));
            },
            resetFilter() {
                this.InitialHomePrice = Accounting.formatNumber(this.homePrice, { precision: 0 });
                this.HomePrice = Accounting.formatNumber(this.homePrice, { precision: 0 });
                this.InitialDownPayment = Accounting.formatNumber(this.downPayment);
                this.DownPayment = Accounting.formatNumber(this.downPayment);
                this.DownPaymentChange(this.DownPayment);
                this.LoanTermInYears = this.loanTermInYears;
                this.MortgageRate = Accounting.formatNumber(this.mortgageRate, { precision: 3 });
                this.PropertyTaxes = Accounting.formatNumber(this.getDollarAmount(this.propertyTaxes));
                this.InitialPropertyTax = this.PropertyTaxes;
                this.PropertyTaxesChange(this.PropertyTaxes);
                this.PropertyTaxesPercent = Accounting.formatNumber(this.propertyTaxesPercent, { precision: 2 });
                this.InitialPropertyTaxPercent = this.PropertyTaxesPercent;
                this.HomeInsurance = Accounting.formatNumber(this.homeInsurance);
                this.HoaFees = parseInt(this.hoaFees, 10);
                this.CreditScore = this.creditScore;
                this.DownPaymentPercent = Accounting.formatNumber(this.downPaymentPercent, { precision: 2 });
                this.InitialDownPaymentPercent = this.DownPaymentPercent;
                this.CheckPMI = !(this.downPaymentPercent >= 20);
                this.IsPmiInclude = this.CheckPMI;
                this.updateMortgageRate(this.LoanTermInYears);
            },
            updateMortgageRate(value) {
                if (value === 30) TokenService.GetTokenValue('30_FIXED_RATE_250K', this.updateMortgageRateValue);
                else TokenService.GetTokenValue('15_FIXED_RATE_250K', this.updateMortgageRateValue);
            },
            updateHomeInsurance() {
                const updatedHomeInsurance = this.unformat(this.HomePrice) * (this.HomeInsurancePerc / 100);
                this.HomeInsurance = Accounting.formatNumber(updatedHomeInsurance);
                this.DownPaymentPercentChange(this.unformat(this.DownPaymentPercent));
                this.PropertyTaxesPercentChange(this.unformat(this.PropertyTaxesPercent));
                if (this.unformat(this.InitialHomePrice) !== this.unformat(this.HomePrice)) this.InitialHomePrice = this.HomePrice;
            },
            capitalize(value) {
                if (!value) return '';

                const data = value.toString();
                return data.charAt(0).toUpperCase() + data.slice(1);
            },
            currencyFilterWithPrecision(figure) {
                return figure > 0 ? Accounting.formatNumber(figure, { precision: 2 }) : 0;
            },
            updateLocation(val) {
                val.city = val.city.toLowerCase();
                val.city = StringFormat.capitalize(val.city);
            },
            checkLocationResponse(val) {
                this.updateLocation(val);
                this.TempLocData = val;
            },
            InitializeData() {
                const updatedHomeInsurance = this.unformat(this.HomePrice) * (this.HomeInsurancePerc / 100);
                this.HomeInsurance = Accounting.formatNumber(updatedHomeInsurance);
                this.DownPaymentPercentChange(this.DownPaymentPercent);
                this.PropertyTaxesPercentChange(this.PropertyTaxesPercent);
            },
            PropertyTaxesChange(value) {
                let propertyTaxesValue = this.unformat(value);
                if (propertyTaxesValue !== this.unformat(this.InitialPropertyTax)) {
                    if (propertyTaxesValue > this.unformat(this.HomePrice)) {
                        propertyTaxesValue = this.unformat(this.HomePrice);
                        this.PropertyTaxes = Accounting.formatNumber(propertyTaxesValue);
                    } else if (propertyTaxesValue > this.unformat(this.maxValue)) {
                        this.PropertyTaxes = this.maxValue;
                        propertyTaxesValue = this.maxValue;
                    }
                    propertyTaxesValue = parseFloat(propertyTaxesValue).toFixed(2);
                    this.PropertyTaxesPercent = this.getPercentageAmount(propertyTaxesValue);
                    this.InitialPropertyTaxPercent = this.PropertyTaxesPercent;
                    this.InitialPropertyTax = this.PropertyTaxes;
                }
            },
            PropertyTaxesPercentChange(value) {
                let propertyTaxesPercentValue = this.unformat(value);
                if (propertyTaxesPercentValue !== this.unformat(this.InitialPropertyTaxPercent) || this.unformat(this.InitialHomePrice) !== this.unformat(this.HomePrice)) {
                    if (this.unformat(this.propertyTaxesPercent) <= 100) {
                        propertyTaxesPercentValue = parseFloat(propertyTaxesPercentValue).toFixed(2);
                        this.PropertyTaxes = Accounting.formatNumber(this.getDollarAmount(propertyTaxesPercentValue));
                    }
                    if (this.unformat(this.PropertyTaxes) > this.unformat(this.HomePrice)) {
                        this.PropertyTaxes = Accounting.formatNumber(this.HomePrice);
                    }
                    this.InitialPropertyTax = this.PropertyTaxes;
                    this.InitialPropertyTaxPercent = this.PropertyTaxesPercent;
                }
            },
            DownPaymentChange(value) {
                let downPaymentValue = this.unformat(value);
                if (downPaymentValue !== this.unformat(this.InitialDownPayment)) {
                    if (downPaymentValue > this.unformat(this.HomePrice)) {
                        downPaymentValue = this.unformat(this.HomePrice);
                        this.DownPayment = Accounting.formatNumber(downPaymentValue);
                    } else if (downPaymentValue > this.unformat(this.maxValue)) {
                        this.DownPayment = this.maxValue;
                        downPaymentValue = this.maxValue;
                    }
                    downPaymentValue = parseFloat(downPaymentValue).toFixed(2);
                    this.DownPaymentPercent = this.getPercentageAmount(downPaymentValue);
                    this.InitialDownPaymentPercent = this.DownPaymentPercent;
                    this.InitialDownPayment = this.DownPayment;
                }
            },
            DownPaymentPercentChange(value) {
                let downPaymentPercentValue = this.unformat(value);
                if (downPaymentPercentValue !== this.unformat(this.InitialDownPaymentPercent) || this.unformat(this.InitialHomePrice) !== this.unformat(this.HomePrice)) {
                    if (this.unformat(this.downPaymentPercent) <= 100) {
                        downPaymentPercentValue = parseFloat(downPaymentPercentValue).toFixed(2);
                        this.DownPayment = Accounting.formatNumber(this.getDollarAmount(downPaymentPercentValue));
                    }
                    if (this.unformat(this.DownPayment) > this.unformat(this.HomePrice)) {
                        this.DownPayment = Accounting.formatNumber(this.HomePrice);
                    }
                    this.InitialDownPaymentPercent = this.DownPaymentPercent;
                    this.InitialDownPayment = this.DownPayment;
                }
            },
            getDollarAmount(value) {
                let calculatedValue = this.unformat(value) / 100 * this.unformat(this.HomePrice);
                calculatedValue = calculatedValue.toFixed(2) % 1 === 0 ? Math.round(calculatedValue) : calculatedValue.toFixed(2);
                return isNaN(calculatedValue) || calculatedValue === 'Infinity' ? 0 : calculatedValue;
            },
            getPercentageAmount(value) {
                const calculatedValue = this.unformat(value) / this.unformat(this.HomePrice) * 100;
                return isNaN(calculatedValue) || calculatedValue === 'Infinity' ? 0 : calculatedValue.toFixed(2);
            },
        },
        mounted() {
            this.InitializeData();
            this.PaymentFrequency = 'monthly';
            setTimeout(() => {
                if (this.LoanTermInYears === 30) TokenService.GetTokenValue('30_FIXED_RATE_250K', this.updateInitialInterestRate);
                else TokenService.GetTokenValue('15_FIXED_RATE_250K', this.updateInitialInterestRate);
            }, 150);
        },
        watch: {
            getFieldValue() {
                this.calculatePayment(this.MortgageRate);
            },
            CheckPMI() {
                if (this.CheckPMI === true) this.IsPmiInclude = true;
            },
        },
        components: {
            NumberInput,
            DropDown,
            LtLabel,
            VueChart,
            LtLogo,
            AdvertisingDisclosures,
        },
        mixins: [GetLocation, BaseMixin],
    };
</script>
<style src="../../assets/styles/common.scss" lang="scss"></style>
<style src="../../assets/styles/calc-advanced-filter.scss" lang="scss"></style>
<style src="./main.scss" lang="scss"></style>
